/**
 * EmailUploadQueuePage - Display pending spreadsheet uploads received via email
 *
 * Shows a table of auto-processed uploads that users can review.
 * For demo purposes, all uploads show as "Ready for Review" with fake processed results.
 */

import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Trash2, Download, Sparkles } from 'lucide-react';
import paths from '@/paths';
import {
  usePendingUploads,
  useDeletePendingUpload,
} from '@/api/spreadsheet/usePendingUploads';
import { formatDistanceToNow } from 'date-fns';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const EmailUploadQueuePage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, refetch } = usePendingUploads();
  const { mutateAsync: deleteUpload, isPending: isDeleting } =
    useDeletePendingUpload();

  // Split uploads into pending (ready for review) and completed
  const allUploads = data?.uploads || [];
  const pendingUploads = allUploads.filter(
    (u) => u.status !== 'completed' && u.status !== 'cancelled',
  );
  const completedUploads = allUploads.filter((u) => u.status === 'completed');

  const handleViewResults = (uploadId: string) => {
    // Navigate to demo results page
    navigate({
      to: '/rebates/upload-data/demo-results/$uploadId',
      params: { uploadId },
    });
  };

  const handleDelete = async (id: string) => {
    if (
      window.confirm('Are you sure you want to delete this pending upload?')
    ) {
      await deleteUpload({ id });
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold">Email Upload Queue</h1>
              <p className="text-muted-foreground">
                Spreadsheets received via email - auto-processed and ready for
                review
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <LoadingSpinner text="Loading uploads..." minHeight={200} />
        ) : (
          <>
            {/* Ready for Review Section */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4">Ready for Review</h2>
              {pendingUploads.length === 0 ? (
                <div className="text-center py-12 border rounded-lg bg-muted/20">
                  <p className="text-muted-foreground">
                    No uploads pending review
                  </p>
                </div>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>File Name</TableHead>
                        <TableHead>Sender</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>Received</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Proceed</TableHead>
                        <TableHead className="w-[80px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingUploads.map((upload) => (
                        <TableRow key={upload.id}>
                          <TableCell className="font-medium">
                            {upload.originalFileName}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {upload.senderEmail || '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground max-w-[200px] truncate">
                            {upload.emailSubject || '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatFileSize(upload.fileSizeBytes)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDistanceToNow(new Date(upload.createdAt), {
                              addSuffix: true,
                            })}
                          </TableCell>
                          <TableCell>
                            <Badge variant="default" className="bg-amber-500">
                              Ready to ingest
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              onClick={() => handleViewResults(upload.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Sparkles className="h-4 w-4 mr-2" />
                              Begin Ingestion
                            </Button>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {upload.downloadUrl && (
                                  <DropdownMenuItem asChild>
                                    <a
                                      href={upload.downloadUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <Download className="h-4 w-4 mr-2" />
                                      Download Original
                                    </a>
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => handleDelete(upload.id)}
                                  disabled={isDeleting}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            {/* Completed Section */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4">Completed</h2>
              {completedUploads.length === 0 ? (
                <div className="text-center py-8 border rounded-lg bg-muted/10">
                  <p className="text-sm text-muted-foreground">
                    No completed uploads yet
                  </p>
                </div>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>File Name</TableHead>
                        <TableHead>Sender</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>Received</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[80px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {completedUploads.map((upload) => (
                        <TableRow key={upload.id} className="opacity-70">
                          <TableCell className="font-medium">
                            {upload.originalFileName}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {upload.senderEmail || '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground max-w-[200px] truncate">
                            {upload.emailSubject || '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatFileSize(upload.fileSizeBytes)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDistanceToNow(new Date(upload.createdAt), {
                              addSuffix: true,
                            })}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className="border-green-500 text-green-700"
                            >
                              Completed
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => handleDelete(upload.id)}
                                  disabled={isDeleting}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Info Box */}
        <div className="mt-8 p-4 bg-muted/30 rounded-lg">
          <h3 className="font-medium mb-2">How it works</h3>
          <ol className="text-sm text-muted-foreground space-y-1">
            <li>
              1. Send CSV or XLSX files to the designated upload email address
            </li>
            <li>
              2. Files are automatically processed by AI and appear here as
              &quot;Ready for Review&quot;
            </li>
            <li>
              3. Click &quot;Begin Ingestion&quot; to process and review the
              data
            </li>
            <li>4. Submit to create rebate agreements</li>
          </ol>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EmailUploadQueuePage;
